import AddIcon from "@mui/icons-material/AddRounded";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridGroupingColDefOverride,
  GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import StatusChip from "@entrilia/shared/components/StatusChip";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { WorkflowGroup } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import WorkflowTemplateAvatar from "../../../common/WorkflowTemplateAvatar";

const launchWorkflow = withErrorHandling(adminApi.launchWorkflow);

const gridRowGroupingModel: GridRowGroupingModel = ["category"];

const gridColumnVisibilityModel: GridColumnVisibilityModel = {
  category: false,
  name: false,
};

const groupingColumnDefinition: GridGroupingColDefOverride<WorkflowGroup> = {
  leafField: "name",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  flex: 3,
};

const getRowId = (row: WorkflowGroup) => row.templateId;

const NameCell = ({ row }: { row: WorkflowGroup }) => {
  const { clientCode } = useClientContext();

  const namePath =
    row.workflowType === "InvoiceProcessing"
      ? `/${clientCode}/${pageRoutes.expenseManagement}/${pageRoutes.invoices}`
      : row.templateId;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flex: 1, minWidth: 0 }}>
      <Box sx={{ width: "2.5rem" }} />
      <WorkflowTemplateAvatar workflowType={row.workflowType} category={row.category} />
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <GridTextHoverLink to={namePath}>
            <Typography variant="subtitle2" noWrap>
              {row.name}
            </Typography>
          </GridTextHoverLink>
        </Box>
        <Typography variant="caption" noWrap color="textSecondary">
          {row.description}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ActionsCell = ({ row }: { row: WorkflowGroup }) => {
  const { hasPermissions } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const navigate = useNavigate();

  const [launching, setLaunching] = useState(false);

  const canManageWorkflows = hasPermissions(["ManageWorkflows"]);

  const handleNewRunClick = async () => {
    setLaunching(true);
    const [workflowDetails, error] = await launchWorkflow({ workflowType: row.workflowType });
    setLaunching(false);

    if (error) {
      logError(error, "[WorkflowGroupsGrid] launchWorkflow");
      sendNotificationError(error.message);
      return;
    }

    navigate(`${row.templateId}/${workflowDetails.workflow.id}`);
  };

  return (
    <>
      {canManageWorkflows && row.allowManualLaunch && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewRunClick}
          loading={launching}
          sx={{ mr: 1 }}
        >
          New Run
        </Button>
      )}
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<SettingsIcon />}
        onClick={() => navigate(`../${pageRoutes.workflowTemplates}/${row.latestTemplateVersionId}`)}
      >
        Template
      </Button>
    </>
  );
};

const columnDefinitions: GridColDef<WorkflowGroup>[] = [
  {
    field: nameof<WorkflowGroup>("category"),
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        return <Typography variant="subtitle2">{stringifyValue(rowNode.groupingKey)}</Typography>;
      }

      return null;
    },
  },
  {
    field: nameof<WorkflowGroup>("name"),
    headerName: "Name",
    flex: 3,
    minWidth: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <NameCell row={row} />;
    },
  },
  {
    field: nameof<WorkflowGroup>("workflowsRequiringUserActionCount"),
    headerName: "",
    flex: 1,
    minWidth: 160,
    align: "center",
    headerAlign: "center",
    renderCell: ({ rowNode, row }) => {
      if (rowNode.type === "group") {
        return null;
      }

      if (row.workflowsRequiringUserActionCount === 0) {
        return null;
      }

      return (
        <StatusChip
          label={`Need Action: ${row.workflowsRequiringUserActionCount}`}
          color={(t) => t.palette.warning.main}
        />
      );
    },
  },
  {
    field: nameof<WorkflowGroup>("workflowChannelEmail"),
    headerName: "Channel",
    flex: 3,
    minWidth: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      if (!row.workflowChannelEmail) {
        return null;
      }

      return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", width: "100%" }}>
          <Typography noWrap>{row.workflowChannelEmail}</Typography>
          <CopyToBufferButton textToCopy={row.workflowChannelEmail} />
        </Stack>
      );
    },
  },
  {
    field: nameof<WorkflowGroup>("inProgressWorkflowsCount"),
    headerName: "Active Runs",
    flex: 1,
    minWidth: 80,
    type: "number",
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 240,
    align: "right",
    headerAlign: "right",
    renderCell: ({ rowNode, row }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <ActionsCell row={row} />;
    },
  },
];

interface Props {
  rows: WorkflowGroup[];
  loading?: boolean;
}

const WorkflowGroupsGrid = ({ rows, loading }: Props) => {
  const apiRef = useGridApiRef();

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  return (
    <DataGrid<WorkflowGroup>
      apiRef={apiRef}
      rows={rows}
      getRowId={getRowId}
      columns={columnDefinitions}
      groupingColDef={groupingColumnDefinition}
      rowGroupingModel={gridRowGroupingModel}
      columnVisibilityModel={gridColumnVisibilityModel}
      loading={loading}
      isGroupExpandedByDefault={isGroupExpanded}
      noRowsText="No workflow groups"
      multilineRows
      disableColumnPinning
      disableColumnReorder
      disableColumnSorting
    />
  );
};

export default WorkflowGroupsGrid;
